import {Fragment, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {PageOutdatedStatus, PageStatus} from '@tinct/core-api-models';
import {MegaphoneIcon, ShieldCheck, SkipForward, SquareCode, Target} from 'lucide-react';
import type {AdsProviderType} from '@tinct/core-api-models';
import {Avatar, AvatarFallback, AvatarImage, Button, LinkedinAdsTrackingCallout, LongText, Separator} from '@tinct/web-components';
import {useCurrentWorkspaceUrlPrefix} from '@tinct/core-workspace';
import {PageStatusBadge, PageOutdatedStatusBadge, PageTargetBadge, getPageDisplayIdentity, type PageTargetType} from '../../../pages/shared';
import {getAdsIntegrationPath, useAdsIntegrations} from '../../../hooks/use-ads-integrations';

const INITIAL_PAGE_SIZE = 20;

export type Step7PublishPage = {
  id: string | null;
  status: PageStatus | null;
  outdatedStatus: PageOutdatedStatus | null;
  targetType?: PageTargetType | null;
  adCampaign?: {name: string | null; integrationId?: string | null} | null;
  lead: {
    company: {
      name: string | null;
      logo: {url: string | null} | null;
    } | null;
  } | null;
};

type Step7PublishContentProps = {
  pages: Step7PublishPage[];
  readyCount: number;
  republishCount: number;
  /** Pages taken offline that this publish brings back live. */
  unpublishedCount?: number;
  excludedCount: number;
  waitingCount: number;
  /** NEW-origin campaigns run on their Tinct-hosted page — no snippet to install. */
  isNewOrigin?: boolean;
  /** ADWORD campaigns: variants come from matched ads campaigns (Google Ads / LinkedIn Ads). */
  isAdwordAxis?: boolean;
};

const HIGHLIGHTS: {icon: typeof SquareCode; title: string[]; body: string}[] = [
  {
    icon: SquareCode,
    title: ['No code', 'no rebuilds'],
    body: 'One snippet on your site. No developer, no DNS, no deploy cycle.',
  },
  {
    icon: ShieldCheck,
    title: ['Your original page stays intact'],
    body: 'Variant content is layered in at runtime. The page in your CMS is never modified.',
  },
  {
    icon: Target,
    title: ['Seen only by your audience'],
    body: 'Visitors matched to your audience list see a personalized variant. Everyone else sees your standard page.',
  },
  {
    icon: SkipForward,
    title: ['Preview, pause, expand anytime'],
    body: 'Preview any variant, pause the campaign, or add new accounts. Changes go live the moment you save.',
  },
];

const STATUS_SORT_ORDER: Record<PageStatus, number> = {
  ready: 0,
  generating: 1,
  waiting: 2,
  queued: 3,
  error: 4,
  unpublished: 5,
  draft: 6,
  published: 7,
  excluded: 8,
  excluded_conflict: 9,
};

const pluralize = (n: number, singular: string, plural: string) =>
  n === 1 ? singular : plural;

const CompanyCell = ({
  page,
  providerByIntegrationId,
}: {
  page: Step7PublishPage;
  providerByIntegrationId?: Map<string, AdsProviderType>;
}) => {
  const company = page.lead?.company;
  const {label: name, targetType} = getPageDisplayIdentity(page);
  const integrationId = page.adCampaign?.integrationId;
  const providerType = integrationId ? providerByIntegrationId?.get(integrationId) : undefined;
  const logoUrl = company?.logo?.url;
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="flex items-center gap-2">
      <Avatar className="size-6 rounded-md">
        {logoUrl && <AvatarImage src={logoUrl} alt={name} />}
        <AvatarFallback className="rounded-md text-xs">
          {targetType === 'ad_campaign' ? (
            <MegaphoneIcon className="size-3.5 text-muted-foreground" />
          ) : (
            initials
          )}
        </AvatarFallback>
      </Avatar>
      <LongText className="font-medium">{name}</LongText>
      <PageTargetBadge targetType={targetType} providerType={providerType} />
    </div>
  );
};

/**
 * ADWORD campaigns with no variant yet aren't stuck — matching happens at
 * every ads sync. Say so and point at the integration (or its setup).
 */
const AdwordNoMatchPanel = () => {
  const workspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const {integrations} = useAdsIntegrations();

  return (
    <div className="flex flex-col items-center gap-3 rounded-2xl border border-border px-6 py-10 text-center">
      <MegaphoneIcon className="size-7 text-muted-foreground" />
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-foreground">
          No ads campaign (Google Ads / LinkedIn Ads) matches this page yet
        </p>
        <p className="max-w-md text-sm text-muted-foreground">
          Variants are created automatically when a synced ads campaign points
          at this page — you can launch now and they&apos;ll follow.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {integrations.length > 0 ? (
          integrations.map((integration) => (
            <Button key={integration.id} variant="outline" size="sm" asChild>
              <Link to={getAdsIntegrationPath(workspaceUrlPrefix, integration)}>
                Open {integration.providerType === 'linkedin_ads' ? 'LinkedIn Ads' : 'Google Ads'} integration
              </Link>
            </Button>
          ))
        ) : (
          <Button variant="outline" size="sm" asChild>
            <Link to={`${workspaceUrlPrefix}/settings/integrations`}>
              Connect an ads integration
            </Link>
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * How the edge picks an ad variant: the `tinct_ad` query parameter carrying
 * the ads campaign id. Google Ads fills it through its `{campaignid}` URL
 * macro; LinkedIn through a dynamic tracking parameter set once in Campaign
 * Manager (same callout as the LinkedIn integration page).
 */
const AdParameterHelp = ({showLinkedin}: {showLinkedin: boolean}) => (
  <div className="flex flex-col gap-3">
    <div className="flex flex-col gap-1 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
      <p className="font-medium text-foreground">Ad parameter</p>
      <p>
        Visitors are routed to their variant by the{' '}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">tinct_ad</code>{' '}
        query parameter on the landing URL, set to the ads campaign id.
      </p>
      <p>
        Google Ads: use the{' '}
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{'{campaignid}'}</code>{' '}
        URL macro (
        <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">{'?tinct_ad={campaignid}'}</code>
        ) in the final URL suffix.
      </p>
    </div>
    {showLinkedin && <LinkedinAdsTrackingCallout />}
  </div>
);

export const Step7PublishContent = ({
  pages,
  readyCount,
  republishCount,
  unpublishedCount = 0,
  excludedCount,
  waitingCount,
  isNewOrigin = false,
  isAdwordAxis = false,
}: Step7PublishContentProps) => {
  // Resolves each ad page's integration to its provider (Google / LinkedIn)
  // for the row badge; empty when no ads flag is on.
  const {providerByIntegrationId, linkedinAdsEnabled} = useAdsIntegrations();
  // The hosted page already carries the snippet — swap the install framing.
  let highlights = isNewOrigin
    ? [
        {
          icon: SquareCode,
          title: ['Nothing to install'],
          body: 'The Tinct snippet ships inside your hosted page. No developer, no DNS, no deploy cycle.',
        },
        ...HIGHLIGHTS.slice(1).map((highlight) =>
          highlight.icon === ShieldCheck
            ? {
                ...highlight,
                title: ['Your hosted page stays intact'],
                body: 'Variant content is layered in at runtime. Your published page is never modified.',
              }
            : highlight
        ),
      ]
    : HIGHLIGHTS;
  // ADWORD campaigns: variants follow the ads campaign that brought the
  // visitor, not an audience list — swap the two audience-flavored cards.
  if (isAdwordAxis) {
    highlights = highlights.map((highlight) => {
      if (highlight.icon === Target) {
        return {
          icon: Target,
          title: ["Seen by your ads' visitors"],
          body: 'Visitors arriving from a matched ads campaign (Google Ads / LinkedIn Ads) see its variant. Everyone else sees your standard page.',
        };
      }
      if (highlight.icon === SkipForward) {
        return {
          ...highlight,
          body: 'Preview any variant or pause the campaign — new ads campaigns get variants automatically at every sync.',
        };
      }
      return highlight;
    });
  }

  const tablePages = [...pages].sort(
    (a, b) =>
      (a.status ? STATUS_SORT_ORDER[a.status] : 99) -
      (b.status ? STATUS_SORT_ORDER[b.status] : 99)
  );

  const [visibleCount, setVisibleCount] = useState(INITIAL_PAGE_SIZE);
  const visiblePages = tablePages.slice(0, visibleCount);
  const hasMore = visibleCount < tablePages.length;
  const sentinelRef = useRef<HTMLTableRowElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || !sentinelRef.current || !scrollContainerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleCount((c) => Math.min(c + INITIAL_PAGE_SIZE, tablePages.length));
        }
      },
      {root: scrollContainerRef.current, rootMargin: '100px'}
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, tablePages.length]);

  return (
    <div className="flex w-full flex-col items-center px-16 pb-24 pt-16">
      <div className="flex w-full max-w-[896px] flex-col gap-12">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col">
            <h1 className="font-heading text-3xl font-semibold leading-9 tracking-[-0.5px] text-foreground">
              Ready to launch!
            </h1>
            <h1 className="font-heading text-3xl font-semibold leading-9 tracking-[-0.5px] text-ring">
              {isAdwordAxis
                ? pages.length === 0
                  ? 'Launch now — variants go live as your ads campaigns match.'
                  : 'Your ad page variants are ready to go live.'
                : 'Your personalized pages are ready to go live.'}
            </h1>
          </div>
          <p className="max-w-[640px] text-base text-muted-foreground">
            {readyCount > 0 && (
              <>
                You&apos;re about to publish {readyCount}{' '}
                {pluralize(readyCount, 'page', 'pages')}.{' '}
              </>
            )}
            {republishCount > 0 && (
              <>
                {republishCount} outdated{' '}
                {pluralize(republishCount, 'page', 'pages')} will be updated.{' '}
              </>
            )}
            {unpublishedCount > 0 && (
              <>
                {unpublishedCount}{' '}
                {pluralize(unpublishedCount, 'page was', 'pages were')}{' '}
                unpublished and will be republished.{' '}
              </>
            )}
            {excludedCount > 0 && (
              <>
                {excludedCount}{' '}
                {pluralize(excludedCount, 'page has', 'pages have')} been
                excluded and won&apos;t be published.{' '}
              </>
            )}
            {waitingCount > 0 && (
              <>
                {waitingCount}{' '}
                {pluralize(waitingCount, 'page hasn’t', 'pages haven’t')} been
                generated yet.
              </>
            )}
          </p>
        </div>

        {/* Badges + Table */}
        <div className="flex flex-col gap-5">
          <div className="flex gap-1">
            {readyCount > 0 && (
              <PageStatusBadge status="ready" label={`${readyCount} ready`} />
            )}
            {republishCount > 0 && (
              <PageOutdatedStatusBadge
                status="needs_republication"
                label={`${republishCount} outdated`}
              />
            )}
            {unpublishedCount > 0 && (
              <PageStatusBadge
                status="unpublished"
                label={`${unpublishedCount} to republish`}
              />
            )}
            {excludedCount > 0 && (
              <PageStatusBadge status="excluded" label={`${excludedCount} excluded`} />
            )}
            {waitingCount > 0 && (
              <PageStatusBadge status="waiting" label={`${waitingCount} waiting`} />
            )}
          </div>

          {isAdwordAxis && <AdParameterHelp showLinkedin={linkedinAdsEnabled} />}

          {isAdwordAxis && pages.length === 0 ? (
            <AdwordNoMatchPanel />
          ) : (
          <div className="overflow-hidden rounded-2xl border border-border">
            <div ref={scrollContainerRef} className="max-h-[320px] overflow-y-auto">
              <table className="w-full border-collapse">
                <thead className="sticky top-0 z-10 bg-background">
                  <tr className="border-b border-border">
                    <th className="h-12 px-3 text-left text-sm font-medium text-foreground">
                      {isAdwordAxis ? 'Ads campaign' : 'Company'}
                    </th>
                    <th className="h-12 w-[234px] px-3 text-left text-sm font-medium text-foreground">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePages.map((page) => (
                    <tr key={page.id} className="border-b border-border last:border-b-0">
                      <td className="h-12 px-3 py-1">
                        <CompanyCell page={page} providerByIntegrationId={providerByIntegrationId} />
                      </td>
                      <td className="h-12 px-3 py-2">
                        <div className="flex flex-wrap items-center gap-1">
                          <PageStatusBadge status={page.status} />
                          <PageOutdatedStatusBadge status={page.outdatedStatus} />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {hasMore && (
                    <tr ref={sentinelRef} aria-hidden="true">
                      <td colSpan={2} className="h-px" />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </div>

        {/* What happens when you launch */}
        <section className="flex flex-col gap-8">
          <h2 className="font-heading text-2xl font-semibold leading-8 tracking-[-0.4px] text-foreground">
            What happens when you launch?
          </h2>
          <div className="flex items-start gap-6 rounded-2xl border border-border px-6 py-6">
            {highlights.map(({icon: Icon, title, body}, idx) => (
              <Fragment key={title.join(' ')}>
                <div className="flex flex-1 flex-col gap-3">
                  <Icon className="size-6 text-foreground" strokeWidth={2} />
                  <div className="text-base font-semibold leading-[23px] text-foreground">
                    {title.map((line) => (
                      <p key={line}>{line}</p>
                    ))}
                  </div>
                  <p className="text-sm leading-5 text-muted-foreground">{body}</p>
                </div>
                {idx < highlights.length - 1 && (
                  <Separator orientation="vertical" className="h-auto self-stretch" />
                )}
              </Fragment>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};
